// File: LevelWeightage.tsx
// Description: This component allows users to configure weightages for different categories at a specific level.
// This is a temerory file we need that file reference in future

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Paper, Grid, Stack, Dialog, DialogTitle, DialogContent, DialogActions, FormHelperText, CircularProgress, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { showSuccessSnackbar, showErrorSnackbar } from '../../utils/Snackbar';
import { fetchCategories, Category } from '../Category/CategoryService';
import { CommHandler } from '../../utils';
import { config } from '../../config';

interface WeightageItem {
  id: number;
  category: string;
  weightage: string | number; // Allow string for empty state
}

const LevelWeightage: React.FC = () => {
  const navigate = useNavigate();
  const { levelId } = useParams<{ levelId: string }>();

  // Decode the level ID to get the full level string
  const decodedLevelId = levelId ? decodeURIComponent(levelId) : '';
  const levelNumber = decodedLevelId ? decodedLevelId.match(/^(\d+):/)?.[1] || '' : '';

  // State for full level name - initialize with decoded value
  const [fullLevelName, setFullLevelName] = useState<string>(decodedLevelId || '');

  // Start with a reasonable default weightage
  const [weightageItems, setWeightageItems] = useState<WeightageItem[]>([{ id: 1, category: '', weightage: '' }]);
  const [nextId, setNextId] = useState(2);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [totalWeightage, setTotalWeightage] = useState(0);

  // Replace hardcoded categories with fetched categories
  const [availableCategories, setAvailableCategories] = useState<Category[]>([]);

  // Loading state for fetching saved data
  const [isLoadingSavedData, setIsLoadingSavedData] = useState(false);

  // State to track if we're editing existing data
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        const categories = await fetchCategories();
        setAvailableCategories(categories.filter((category: any) => category.status === 1));
      } catch (error) {
        console.error('Error fetching categories:', error);
        showErrorSnackbar('Failed to load categories');
      }
    };

    const fetchFullLevelName = async () => {
      try {
        const response = await CommHandler.request({
          url: `${config.apiBaseUrl}/evaluation/designation-group/levels`,
          method: 'GET',
        });

        if (response.data && Array.isArray(response.data)) {
          const availableLevels = response.data;
          // Find the level that matches our levelNumber
          const matchingLevel = availableLevels.find((level: string) => {
            const match = level.match(/^(\d+):/);
            return match && match[1] === levelNumber;
          });

          if (matchingLevel) {
            setFullLevelName(matchingLevel);
          }
        }
      } catch (error) {
        console.error('Error fetching level data:', error);
        // Keep the original levelId as fallback
      }
    };

    fetchCategoryData();
    if (decodedLevelId) {
      setFullLevelName(decodedLevelId);
    } else if (levelNumber) {
      fetchFullLevelName();
    }
  }, [decodedLevelId, levelNumber]);

  const fetchSavedLevelData = useCallback(async () => {
    if (!fullLevelName) return;

    setIsLoadingSavedData(true);
    try {
      const response = await CommHandler.get('/slab-mapping-metrics/metric');

      if (response && response.data && Array.isArray(response.data)) {
        const currentLevelData = response.data.filter((item: any) => item.level === fullLevelName);

        if (currentLevelData.length > 0) {
          const mappedWeightageItems: WeightageItem[] = currentLevelData.map((item: any, index: number) => ({
            id: index + 1,
            category: item.weightage?.category?.name || '',
            weightage: item.weightage?.weightage === 0 || item.weightage?.weightage === undefined ? '' : item.weightage?.weightage,
          }));

          setWeightageItems(mappedWeightageItems);
          setNextId(mappedWeightageItems.length + 1);
          setIsEditMode(true);
        }
      }
    } catch (error) {
      console.error('Error fetching saved level data:', error);
      const localStorageKey = `levelWeightage_${fullLevelName}`;
      const localData = localStorage.getItem(localStorageKey);
      if (localData) {
        try {
          const parsedData = JSON.parse(localData);
          if (parsedData.weightageItems) {
            setWeightageItems(parsedData.weightageItems);
            setNextId(parsedData.weightageItems.length + 1);
          }
        } catch (parseError) {
          console.error('Error parsing local storage data:', parseError);
        }
      }
    } finally {
      setIsLoadingSavedData(false);
    }
  }, [fullLevelName]);

  useEffect(() => {
    fetchSavedLevelData();
  }, [fetchSavedLevelData]);

  useEffect(() => {
    // Calculate total weightage whenever items change
    const sum = weightageItems.reduce((acc, item) => acc + (typeof item.weightage === 'number' ? item.weightage : 0), 0);
    setTotalWeightage(sum);
  }, [weightageItems]);

  useEffect(() => {
    const title = document.querySelector('title');
    if (title) {
      title.innerText = fullLevelName ? `${fullLevelName} Weightage Configuration` : 'Level Weightage Configuration';
    }

    // Redirect to slab mapping if no level ID is provided
    if (!levelId) {
      navigate('/evaluation', { state: { tabIndex: 1 } });
    }
  }, [fullLevelName, levelId, navigate]);

  const handleAddCategory = () => {
    setWeightageItems([...weightageItems, { id: nextId, category: '', weightage: '' }]);
    setNextId(nextId + 1);
  };

  const handleRemoveCategory = (id: number) => {
    if (weightageItems.length > 1) {
      setWeightageItems(weightageItems.filter((item) => item.id !== id));
    }
  };

  const handleCategoryChange = (id: number, value: string) => {
    setWeightageItems((prevItems) => prevItems.map((item) => (item.id === id ? { ...item, category: value } : item)));
  };

  const handleWeightageChange = (id: number, value: string) => {
    // Allow empty string for clearing the field
    if (value === '') {
      setWeightageItems((prevItems) => prevItems.map((item) => (item.id === id ? { ...item, weightage: '' } : item)));
      return;
    }
    const numValue = parseInt(value, 10);

    if (Number.isNaN(numValue) || numValue < 0) {
      return;
    }

    if (numValue > 100) {
      showErrorSnackbar('Weightage cannot exceed 100%');
      return;
    }

    setWeightageItems((prevItems) => prevItems.map((item) => (item.id === id ? { ...item, weightage: numValue } : item)));
  };

  const handleCancel = () => {
    setShowConfirmDialog(true);
  };

  const handleSave = async () => {
    const levelNum = parseInt(levelNumber, 10);
    if (Number.isNaN(levelNum) || levelNum <= 0 || levelNum > 99) {
      showErrorSnackbar('Invalid level number');
      return;
    }
    if (totalWeightage !== 100) {
      showErrorSnackbar('Total weightage must equal 100%');
      return;
    }

    // Build the payload as required
    let payload: any[] = [];
    if (isEditMode) {
      // Fetch metric data to get weightage_id for each category in this level
      try {
        const metricRes = await CommHandler.get('/slab-mapping-metrics/metric');
        let metricData: any[] = [];
        if (metricRes && metricRes.data && Array.isArray(metricRes.data)) {
          metricData = metricRes.data.filter((item: any) => item.level === fullLevelName);
        }
        payload = weightageItems.map((item) => {
          const selectedCategory = availableCategories.find((cat) => cat.name === item.category);
          const metricMatch = metricData.find((m) => m.weightage?.category_id === selectedCategory?.id);
          return {
            weightage_id: metricMatch ? metricMatch.weightage_id : null,
            category_id: selectedCategory?.id || 1,
            weightage: item.weightage,
            allDepartment: true,
          };
        });
      } catch (err) {
        showErrorSnackbar('Failed to fetch metric data for update');
        return;
      }
    } else {
      payload = weightageItems.map((item) => {
        const selectedCategory = availableCategories.find((cat) => cat.name === item.category);
        return {
          weightage_id: null,
          category_id: selectedCategory?.id || 1,
          weightage: item.weightage,
          allDepartment: true,
        };
      });
    }

    try {
      if (isEditMode) {
        await CommHandler.put(`/slab-mapping-metrics/level/${encodeURIComponent(fullLevelName)}`, payload);
        showSuccessSnackbar('Weightages and level mapping are successfully updated.');
        setTimeout(() => {
          navigate('/evaluation', { state: { tabIndex: 1 } });
        }, 1500);
      } else {
        const response = await CommHandler.post('/slab-mapping-metrics/weightage', payload);
        if (response && response.data && Array.isArray(response.data)) {
          const weightageIds = response.data.map((item: any) => item?.id) || [];
          if (!weightageIds || weightageIds.length === 0) {
            showErrorSnackbar('No weightage IDs received from the API response');
            return;
          }
          const parsedLevelNum = parseInt(levelNumber, 10);
          if (Number.isNaN(parsedLevelNum) || parsedLevelNum <= 0) {
            showErrorSnackbar('Invalid level number');
            return;
          }
          try {
            const metricPayload = {
              level: fullLevelName, // Use full level name instead of just number
              weightage_id: weightageIds,
            };
            const metricResponse = await CommHandler.post('/slab-mapping-metrics/metric', metricPayload);
            if (metricResponse) {
              showSuccessSnackbar('Weightages and level mapping are successfully saved.');
              setTimeout(() => {
                navigate('/evaluation', { state: { tabIndex: 1 } });
              }, 1500);
            } else {
              showErrorSnackbar('Weightages saved but level mapping failed. Please try again.');
            }
          } catch (metricError) {
            showErrorSnackbar('Weightages saved but level mapping failed');
          }
        } else {
          showErrorSnackbar('Failed to save weightages');
        }
      }
    } catch (error) {
      showErrorSnackbar('An unexpected error occurred');
    }
  };

  const handleConfirmCancel = () => {
    setShowConfirmDialog(false);
    navigate('/evaluation', { state: { tabIndex: 1 } });
  };

  const handleCloseConfirmDialog = () => {
    setShowConfirmDialog(false);
  };

  // Get remaining available categories that haven't been selected yet
  const getAvailableCategories = (currentId: number) => {
    const selectedCategories = weightageItems.filter((item) => item.id !== currentId).map((item) => item.category);

    return availableCategories.filter((category) => !selectedCategories.includes(category.name));
  };

  // If levelId doesn't exist, this is a safety check
  if (!levelId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          No level selected. Redirecting to Slab Mapping...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" mb={3} spacing={2}>
        <IconButton onClick={() => handleCancel()}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {fullLevelName ? `${fullLevelName} Weightage Configuration` : 'Level Weightage Configuration'}
        </Typography>
        {isLoadingSavedData && <CircularProgress size={24} />}
      </Stack>

      {isLoadingSavedData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading saved weightage data...</Typography>
        </Box>
      ) : (
        <>
          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            {weightageItems.map((item) => (
              <Grid container spacing={2} key={item.id} sx={{ mb: 3 }}>
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Category</InputLabel>
                        <Select value={item.category} label="Category" onChange={(e) => handleCategoryChange(item.id, e.target.value as string)}>
                          {getAvailableCategories(item.id).map((category) => (
                            <MenuItem key={category.id} value={category.name}>
                              {category.name}
                            </MenuItem>
                          ))}
                          {item.category && !getAvailableCategories(item.id).some((cat) => cat.name === item.category) && (
                            <MenuItem key={`current-${item.category}`} value={item.category}>
                              {item.category}
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Weightage %"
                        type="text"
                        value={item.weightage}
                        onChange={(e) => handleWeightageChange(item.id, e.target.value)}
                        fullWidth
                        InputProps={{
                          inputProps: {
                            min: 0,
                            max: 100,
                            style: { textAlign: 'right' },
                          },
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-' || e.key === '.') {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                  {weightageItems.length > 1 && (
                    <Tooltip title="Remove category">
                      <IconButton onClick={() => handleRemoveCategory(item.id)} color="error" sx={{ mt: 1 }} aria-label="Remove category">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
            ))}
            {totalWeightage < 100 && (
              <Box sx={{ mt: 2, mb: 3 }}>
                <Button startIcon={<AddIcon />} variant="outlined" onClick={handleAddCategory}>
                  Add Category
                </Button>
              </Box>
            )}

            <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(0, 0, 0, 0.04)', borderRadius: 1 }}>
              <Typography variant="h6" color={totalWeightage === 100 ? 'primary' : 'error'}>
                Total Weightage: {totalWeightage}%
              </Typography>
              {totalWeightage !== 100 && <FormHelperText error>{totalWeightage < 100 ? 'Total weightage should be 100%' : 'Total weightage exceeds 100%'}</FormHelperText>}
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={totalWeightage !== 100} sx={{ color: '#ffffff' }}>
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </Box>

          {/* Confirmation Dialog */}
          <Dialog open={showConfirmDialog} onClose={handleCloseConfirmDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">Cancel Configuration</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to discard the configurations?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
              <Button onClick={handleConfirmCancel} color="primary" autoFocus>
                Ok
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default LevelWeightage;
